import { Component, Host, Prop, h, Element, Event, EventEmitter, Watch } from '@stencil/core';
import '@material/web/tabs/primary-tab';
import '@material/web/tabs/secondary-tab';
import '@material/web/tabs/tabs';

/** Segmented controls or Tabs organize content across different screens and views.
 * @slot - Slot for <omk-tab> component.
 *
 * @event omkTabChange  - Emitted when the selected tab changes.
 */
@Component({
  tag: 'omk-tabs',
  styleUrl: 'omk-tabs.scss',
  shadow: true,
})
export class OmkTabs {
  /** The variant of the tabs */
  @Prop() variant: 'fixed' | 'scrollable' = 'fixed';

  /** Index of the currently active tab */
  @Prop({ mutable: true }) activeTabIndex: number = 0;

  /** Accessibility label for the tabs container */
  @Prop() ariaLabel: string | null;

  /** Whether the tab text should be in bold or not */
  @Prop() tabEmphasis: 'normal' | 'high' = 'normal';

  @Element() el: HTMLElement;

  /** Emits when the active tab changes */
  @Event() omkTabChange: EventEmitter<{ index: number; tabElement: HTMLOmkTabElement }>;

  handleSlotChange(event) {
    const slottedTabs = event.target.assignedElements();
    slottedTabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        this.activeTabIndex = index;
        this.omkTabChange.emit({ index: this.activeTabIndex, tabElement: tab });
        this.updateActiveTab();
      });
    });

    this.updateActiveTab();
    this.updateTabEmphasis();
  }

  /** Watch for changes to the activeTabIndex property */
  @Watch('activeTabIndex')
  handleActiveTabIndexChange() {
    this.updateActiveTab();
  }

  /** Watch for changes to the tabEmphasis property */
  @Watch('tabEmphasis')
  handleTabEmphasisChange() {
    this.updateTabEmphasis();
  }

  /** Update the active state of slotted tabs */
  updateActiveTab() {
    const slottedTabs = this.el.querySelectorAll<HTMLOmkTabElement>('omk-tab');

    slottedTabs.forEach((tab, index) => {
      if (index === this.activeTabIndex) {
        tab.setAttribute('active', 'true');
      } else {
        tab.removeAttribute('active');
      }
    });
  }

  /** Update the emphasis property of slotted tabs */
  updateTabEmphasis() {
    const slottedTabs = this.el.querySelectorAll<HTMLOmkTabElement>('omk-tab');

    slottedTabs.forEach(tab => {
      if (this.tabEmphasis === 'high') {
        tab.tabEmphasis = 'high';
      } else {
        tab.tabEmphasis = 'normal';
      }
    });
  }

  /** Handle change event for the tabs */
  handleTabChange(event: CustomEvent) {
    this.activeTabIndex = event.detail.index;
    const slottedTabs = this.el.querySelectorAll<HTMLOmkTabElement>('omk-tab');
    const activeTabElement = slottedTabs[this.activeTabIndex];

    this.omkTabChange.emit({ index: this.activeTabIndex, tabElement: activeTabElement });
    this.updateActiveTab();
  }

  render() {
    return (
      <Host>
        <md-tabs
          class={{ [this.variant]: true }}
          active-tab-index={this.activeTabIndex}
          aria-label={this.ariaLabel}
          onChange={(event: CustomEvent) => this.handleTabChange(event)}
        >
          <slot onSlotchange={this.handleSlotChange.bind(this)}></slot>
        </md-tabs>
      </Host>
    );
  }
}
